import * as Sentry from '@sentry/react-native';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface LogContext {
  [key: string]: unknown;
}

const LEVEL_PREFIX: Record<LogLevel, string> = {
  debug: '[DEBUG]',
  info: '[INFO]',
  warn: '[WARN]',
  error: '[ERROR]',
  fatal: '[FATAL]',
};

function log(level: LogLevel, tag: string, message: string, context?: LogContext): void {
  const prefix = `${LEVEL_PREFIX[level]} [${tag}]`;

  if (__DEV__) {
    const consoleMethod = level === 'warn' ? 'warn'
      : (level === 'error' || level === 'fatal') ? 'error'
      : 'log';

    if (context) {
      console[consoleMethod](prefix, message, context);
    } else {
      console[consoleMethod](prefix, message);
    }
  }

  // Debug logs are dev-only, never sent to Sentry
  if (level === 'debug') return;

  if (level === 'info') {
    Sentry.addBreadcrumb({
      category: tag,
      message,
      level: 'info',
      data: context as Record<string, unknown> | undefined,
    });
    return;
  }

  if (level === 'warn') {
    Sentry.addBreadcrumb({
      category: tag,
      message,
      level: 'warning',
      data: context as Record<string, unknown> | undefined,
    });
    return;
  }

  // error and fatal
  Sentry.withScope((scope) => {
    scope.setTag('component', tag);
    scope.setLevel(level === 'fatal' ? 'fatal' : 'error');
    if (context) {
      const { error: errorObj, ...extras } = context;
      if (Object.keys(extras).length > 0) {
        scope.setExtras(extras as Record<string, unknown>);
      }
      if (errorObj instanceof Error) {
        Sentry.captureException(errorObj);
      } else {
        // Non-Error objects (e.g. Supabase PostgrestError) — wrap in Error so Sentry
        // groups by message instead of the logger stack frame (avoids "withScope$argument_0" titles)
        if (errorObj !== undefined) {
          scope.setExtra('error', errorObj);
          const e = errorObj as Record<string, unknown>;
          if (e?.code) scope.setExtra('error_code', e.code);
          if (e?.details) scope.setExtra('error_details', e.details);
          if (e?.hint) scope.setExtra('error_hint', e.hint);
        }
        Sentry.captureException(new Error(`[${tag}] ${message}`));
      }
    } else {
      Sentry.captureException(new Error(`[${tag}] ${message}`));
    }
  });
}

export const logger = {
  debug: (tag: string, message: string, context?: LogContext) =>
    log('debug', tag, message, context),

  info: (tag: string, message: string, context?: LogContext) =>
    log('info', tag, message, context),

  warn: (tag: string, message: string, context?: LogContext) =>
    log('warn', tag, message, context),

  error: (tag: string, message: string, context?: LogContext) =>
    log('error', tag, message, context),

  fatal: (tag: string, message: string, context?: LogContext) =>
    log('fatal', tag, message, context),
};
